import { MODELS, findModel } from './catalog.js';
import { estimateFit, recommendQuantForMachine, recommendRuntimeForMachine, scoreFit } from './fit.js';
import type { FitEstimate, MachineProfile, ModelProfile } from './types.js';

export type RankMode =
  | 'best-overall'
  | 'best-coding'
  | 'fastest'
  | 'best-value'
  | 'best-on-machine'
  | 'beginner';

export interface Recommendation {
  model: ModelProfile;
  fit: FitEstimate;
  score: number;
  why: string;
}

export function recommendForMachine(
  machine: MachineProfile,
  mode: RankMode = 'best-on-machine',
  limit = 6,
): Recommendation[] {
  const runtime = recommendRuntimeForMachine(machine);
  const candidates = MODELS.filter((m) => {
    const quant = recommendQuantForMachine(machine, m);
    const fit = estimateFit(machine, m, quant, runtime);
    return fit.verdict !== 'no';
  });

  const scored: Recommendation[] = candidates.map((model) => {
    const quant = recommendQuantForMachine(machine, model);
    const fit = estimateFit(machine, model, quant, runtime);
    const score = scoreCandidate(model, fit, mode);
    return {
      model,
      fit,
      score,
      why: explainRecommendation(model, fit, mode),
    };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit);
}

function scoreCandidate(model: ModelProfile, fit: FitEstimate, mode: RankMode): number {
  const base = scoreFit(fit, model);
  switch (mode) {
    case 'fastest':
      return fit.expectedTps * 2 + (fit.verdict === 'great' || fit.verdict === 'good' ? 10 : 0);
    case 'best-coding':
      return (
        base +
        (model.useCase === 'coder' ? 20 : 0) +
        ({ S: 15, A: 10, B: 5, C: 0, D: -5 }[model.codingTier] ?? 0)
      );
    case 'best-value':
      return base - model.paramsB * 0.8;
    case 'beginner':
      return (
        base +
        (model.codingTier === 'A' || model.codingTier === 'B' ? 8 : 0) +
        (fit.headroomRatio > 1.6 ? 6 : 0) -
        (model.paramsB > 20 ? 4 : 0)
      );
    case 'best-on-machine':
      return base + (fit.verdict === 'great' ? 10 : 0);
    default:
      return base;
  }
}

function explainRecommendation(model: ModelProfile, fit: FitEstimate, mode: RankMode): string {
  if (mode === 'beginner') {
    return `Plain pick: ${model.displayName} at ${fit.quantization}. ${fit.verdictHeadline}, ~${fit.expectedTps} tok/s, ${fit.codingUsefulnessTier}-tier for coding.`;
  }
  if (mode === 'fastest') {
    return `Fastest usable option: ~${fit.expectedTps} tok/s, ${fit.verdictHeadline.toLowerCase()}.`;
  }
  if (mode === 'best-coding') {
    return `Highest coding tier that fits: ${model.codingTier} base, ${fit.codingUsefulnessTier} after quant. ${fit.verdictHeadline}.`;
  }
  if (mode === 'best-value') {
    return `${model.paramsB}B params, ${fit.expectedTps} tok/s, ${fit.headroomRatio}× headroom — efficient for its size.`;
  }
  return `${fit.verdictHeadline}. ${fit.expectedTps} tok/s, ${fit.headroomRatio}× headroom.`;
}

export function compareCandidatesForMachine(machine: MachineProfile, modelIds: string[]): Recommendation[] {
  const runtime = recommendRuntimeForMachine(machine);
  const out: Recommendation[] = [];
  for (const id of modelIds) {
    const model = findModel(id);
    if (!model) continue;
    const quant = recommendQuantForMachine(machine, model);
    const fit = estimateFit(machine, model, quant, runtime);
    out.push({
      model,
      fit,
      score: scoreFit(fit, model),
      why: explainRecommendation(model, fit, 'best-on-machine'),
    });
  }
  return out.sort((a, b) => b.score - a.score);
}
